import React from "react";
import ReasonTooltip from "./ReasonTooltip";
import "./AttributeValue.scss";
import { getNameFromFileAttributeRef } from "@imminently/decisively-core";
import { createPluggable } from "pluggable-react";
import { NodeDisplayType, type TemporalTextProps, getNodeDisplayData } from "../../../utils/NodeDisplayUtils";
import Chip from "../../common/Chip/Chip";
import DownloadFileButton from "../../common/DownloadFileButton/DownloadFileButton";
import Tooltip from "../../common/Tooltip/Tooltip";
import { useTemporalSlice } from "../TemporalSliceProvider/TemporalSliceProvider";

export type LabelSize = "sm" | "md" | "lg";

export interface AttributeValueProps {
  node: any;
  size?: LabelSize;
  showType?: boolean;
  hideTemporal?: boolean;
  labelProps?: React.HTMLAttributes<HTMLSpanElement>;
  temporalTextProps?: TemporalTextProps;
  temporalChartHeight?: number;
  temporalSlice?: string;
  className?: string;
  baseStyles?: boolean;
  showReason?: boolean;
  endAdornment?: React.ReactNode;
}

export const fileRefsWrapClsnm = "file_refs_wrap_r7y3Ylr";

const normalizeTemporalSlice = (value: string | Date | null | undefined): string | undefined => {
  if (!value) return undefined;
  return value instanceof Date ? value.toISOString() : value;
};

const DefaultAttributeValue = (props: AttributeValueProps) => {
  const {
    node,
    size = "md",
    showType = false,
    hideTemporal = false,
    labelProps = {},
    temporalTextProps,
    temporalChartHeight,
    temporalSlice,
    className,
    baseStyles = true,
    showReason = false,
    endAdornment,
  } = props;
  const temporalSliceFromProvider = useTemporalSlice();
  const resolvedTemporalSlice = normalizeTemporalSlice(temporalSlice ?? temporalSliceFromProvider);

  const { value, type, display, tooltip, color, backgroundColor, variant } = getNodeDisplayData(
    node,
    resolvedTemporalSlice,
    {
      temporalTextProps,
      temporalChartHeight,
    },
  );

  const renderFileLabel = (ref: string, index = 0) => {
    const match = ref.match(/^data:id=(.*);base64,([-A-Za-z0-9+/=]*)$/);
    const nameRaw = match ? getNameFromFileAttributeRef(ref) : value;
    const name = nameRaw.length <= 16 ? nameRaw : `${nameRaw.slice(0, 16)}…`;
    return (
      <Tooltip key={index}>
        <Tooltip.Content>{nameRaw}</Tooltip.Content>
        <Tooltip.Trigger asChild={true}>
          <Chip
            variant={variant}
            color={!variant ? color : undefined}
            backgroundColor={!variant ? backgroundColor : undefined}
            style={{ marginLeft: 5, marginRight: 5 }}
            {...labelProps}
          >
            {String(name)}
            <DownloadFileButton
              reference={ref}
              hideName={true}
            />
          </Chip>
        </Tooltip.Trigger>
      </Tooltip>
    );
  };

  if (value?.fileRefs) {
    return (
      <div
        className={fileRefsWrapClsnm}
        data-deci-attribute-value="root"
        data-deci-part="file-refs-wrap"
        data-deci-base-styles={baseStyles ? true : undefined}
      >
        {value.fileRefs.map(renderFileLabel)}
      </div>
    );
  }

  if (value?.set) {
    return (
      <div
        data-deci-attribute-value="root"
        data-deci-part="set-wrap"
        data-deci-base-styles={baseStyles ? true : undefined}
      >
        <div data-deci-part={"set-bracket"}>{value.set.length}[</div>
        {value.set.map((val: any, index: number) => (
          <React.Fragment key={index}>
            {index !== 0 ? <div data-deci-part={"set-comma"}>,</div> : null}
            <AttributeValue
              node={{
                derived: val,
              }}
              size={size}
              baseStyles={baseStyles}
            />
          </React.Fragment>
        ))}
        <div data-deci-part={"set-bracket"}>]</div>
      </div>
    );
  }

  if (type === NodeDisplayType.TEMPORAL) {
    if (hideTemporal) {
      return (
        <Chip
          data-deci-attribute-value="root"
          variant={variant}
          color={!variant ? color : undefined}
          backgroundColor={!variant ? backgroundColor : undefined}
          {...labelProps}
        >
          Temporal
          {endAdornment ? <span data-deci-part="attribute-value-end-adornment">{endAdornment}</span> : null}
        </Chip>
      );
    }
    // is react node
    if (React.isValidElement(display)) {
      return display;
    }
    // else fall through
  }

  const resolvedDebugInfo = node?.debugInfo;
  const reason = showReason ? resolvedDebugInfo?.reason : undefined;
  const tooltipContent = (
    <ReasonTooltip
      tooltip={tooltip}
      reason={reason}
    />
  );
  const hasTooltipContent = Boolean(tooltip || reason?.type);

  return (
    <>
      <Tooltip
        interactable={true}
        visible={!hasTooltipContent ? false : undefined}
      >
        <Tooltip.Trigger asChild>
          <Chip
            data-deci-attribute-value="root"
            variant={variant}
            color={!variant ? color : undefined}
            backgroundColor={!variant ? backgroundColor : undefined}
            className={className}
            {...labelProps}
            data-size={size}
            data-deci-part="attribute-label"
            data-deci-base-styles={baseStyles ? true : undefined}
          >
            {display}
            {endAdornment ? <span data-deci-part="attribute-value-end-adornment">{endAdornment}</span> : null}
          </Chip>
        </Tooltip.Trigger>
        <Tooltip.Content
          style={{
            maxWidth: "unset",
            minWidth: "300px",
          }}
        >
          {tooltipContent}
        </Tooltip.Content>
      </Tooltip>
      {showType && typeof node.input !== "undefined" && (
        <Chip
          style={{ marginLeft: "0.5rem" }}
          variant="info"
        >
          Input
        </Chip>
      )}
    </>
  );
};

const AttributeValue = createPluggable<AttributeValueProps>("deci.attributeValue", DefaultAttributeValue);

export default AttributeValue;
